import React from 'react';
import { NumberInput, TextInput } from '@mantine/core';
import './MetricTableToolbar.css';

export interface PillTabOption {
  value: string;
  label: string;
}

interface MetricPillTabsProps {
  value: string;
  onChange: (value: string) => void;
  options: PillTabOption[];
  'aria-label'?: string;
}

export const MetricPillTabs: React.FC<MetricPillTabsProps> = ({
  value,
  onChange,
  options,
  'aria-label': ariaLabel = 'Opciones de vista',
}) => (
  <div className="metric-pill-tabs" role="tablist" aria-label={ariaLabel}>
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`metric-pill-tabs__pill${isActive ? ' metric-pill-tabs__pill--active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

interface MetricTableMinFilterProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
}

export const MetricTableMinFilter: React.FC<MetricTableMinFilterProps> = ({
  label,
  value,
  onChange,
  min = 1,
}) => (
  <div className="metric-table-filters__field metric-table-filters__field--min">
    <p className="metric-table-filters__label">{label}</p>
    <NumberInput
      classNames={{ input: 'metric-table-filters__input' }}
      value={value}
      onChange={(next) => onChange(Number(next) || min)}
      min={min}
      step={1}
      hideControls={false}
      allowDecimal={false}
      clampBehavior="strict"
    />
  </div>
);

interface MetricTableSearchFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const MetricTableSearchFilter: React.FC<MetricTableSearchFilterProps> = ({
  value,
  onChange,
  placeholder = 'Buscar en la tabla...',
}) => (
  <div className="metric-table-filters__field metric-table-filters__field--search">
    <p className="metric-table-filters__label">Buscar</p>
    <TextInput
      classNames={{ input: 'metric-table-filters__input' }}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  </div>
);

interface MetricTableFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  minValue?: number;
  onMinChange?: (value: number) => void;
  minLabel?: string;
  showMin?: boolean;
}

export const MetricTableFilters: React.FC<MetricTableFiltersProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  minValue,
  onMinChange,
  minLabel = 'Mín. uds. vendidas',
  showMin = false,
}) => {
  if (!showSearch && !showMin) return null;

  return (
    <div className="metric-table-filters">
      {showMin && minValue !== undefined && onMinChange && (
        <MetricTableMinFilter label={minLabel} value={minValue} onChange={onMinChange} />
      )}
      {showSearch && (
        <MetricTableSearchFilter
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
      )}
    </div>
  );
};

interface MetricTableToolbarProps {
  tabs?: PillTabOption[];
  activeTab?: string;
  onTabChange?: (value: string) => void;
  tabAriaLabel?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  minValue?: number;
  onMinChange?: (value: number) => void;
  minLabel?: string;
  showMin?: boolean;
}

export const MetricTableToolbar: React.FC<MetricTableToolbarProps> = ({
  tabs,
  activeTab,
  onTabChange,
  tabAriaLabel,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  minValue,
  onMinChange,
  minLabel,
  showMin = false,
}) => {
  const hasTabs = tabs && tabs.length > 0 && activeTab && onTabChange;
  const hasFilters = showSearch || showMin;

  if (!hasTabs && !hasFilters) return null;

  return (
    <div className="metric-table-toolbar">
      {hasTabs && (
        <MetricPillTabs
          value={activeTab}
          onChange={onTabChange}
          options={tabs}
          aria-label={tabAriaLabel}
        />
      )}
      {hasFilters && (
        <MetricTableFilters
          searchValue={searchValue}
          onSearchChange={onSearchChange ?? (() => undefined)}
          searchPlaceholder={searchPlaceholder}
          showSearch={showSearch && !!onSearchChange}
          minValue={minValue}
          onMinChange={onMinChange}
          minLabel={minLabel}
          showMin={showMin}
        />
      )}
    </div>
  );
};
